import { type IFileListRes, type OauthUserInfo } from '@halo/webauth';
import { GoogleOauth } from '@halo/webauth/googleOauth';
import { isPlainObject } from 'lodash-es';
import { safeJsonParse } from '@halo/utils';
import { WalletCore } from '@halo/wallet-app';
import { userApis } from '@/configs/apis';
import { asymmetricEncryption } from '../walletCloud/helper';
import { getMetadata, setLoginExpiresTime, formatWalletData, formatAccountsData } from './helper';
import type {
  Account,
  uploadArgs,
  SaveAccountArgs,
  DeleteAccountArgs,
  AuthInfo,
  GoogleInitClientArgs,
  AccessInfoArgs,
  Result,
  RequestCloudTokenArgs,
  GoogleTokenInfo,
  Cloud,
  DeleteCloudDataArgs,
} from './types';
import { CloudPlatform, StatusCode } from './types';

const waitForClick = async (dom: HTMLElement, fn: () => Promise<Result>): Promise<Result> => {
  return new Promise((resolve, reject) => {
    try {
      dom?.addEventListener(
        'click',
        async () => {
          const res = await fn();
          resolve(res);
        },
        { once: true }
      );
    } catch (error) {
      reject(error);
    }
  });
};

export class GoogleCloud implements Cloud {
  static client?: GoogleOauth;
  readonly platform: CloudPlatform.googleCloud = CloudPlatform.googleCloud;

  authInfo!: AuthInfo;
  key!: string;
  clientParams!: GoogleInitClientArgs['clientParams'];
  existFiles!: IFileListRes;
  cid?: string = '';

  logout = () => {
    GoogleCloud.client = undefined;
  };

  private getMetadata = (subId?: string) => {
    return getMetadata({ key: this.key, subId });
  };

  setExpiresTime = (cid: string, expiresTime: number) => {
    setLoginExpiresTime({ platform: CloudPlatform.googleCloud, cid, expiresTime });
  };

  requestAuthRedirect = (clientParams: any) => {
    const url = new URL(`https://accounts.google.com/o/oauth2/v2/auth`);
    // const redirect_uri = "http://localhost:7465/login";
    // url.searchParams.set("client_id", VITE_CLIENT_ID_GOOGLE);
    // url.searchParams.set("redirect_uri", redirect_uri);
    // url.searchParams.set("scope", "openid email profile https://www.googleapis.com/auth/drive.appdata");
    // url.searchParams.set("response_type", "code");
    // url.searchParams.set("access_type", "offline");
    // url.searchParams.set("prompt", "");
    const { client_secret, ...restClientParams } = clientParams;
    const clientParams_ = {
      scope: 'openid https://www.googleapis.com/auth/drive.appdata',
      response_type: 'code',
      access_type: 'offline',
      prompt: '',
      ...restClientParams,
    };

    for (const key in clientParams_) {
      url.searchParams.set(key, clientParams_[key]);
    }
    window.location.href = url.toString();
  };

  private requestAccessInfo = async ({
    clientParams,
  }: {
    clientParams: AccessInfoArgs;
  }): Promise<any> => {
    return fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        // code: clientParams.code,
        // client_id: clientParams.client_id,
        // client_secret: clientParams.client_secret,
        // redirect_uri: clientParams.redirect_uri,
        ...clientParams,
      }),
    }).then((res) => res.json());
  };

  requestAccountToken = ({
    oauthClient = GoogleCloud.client,
    clientParams,
  }: {
    oauthClient?: GoogleOauth;
    clientParams: GoogleInitClientArgs['clientParams'];
  }): Promise<GoogleTokenInfo | undefined> => {
    const { requestToken, ...restParams } = clientParams;
    if (!oauthClient) {
      return Promise.reject('Un init client');
    }

    return new Promise((resolve, reject) => {
      oauthClient?.requestCode({
        //client_id: import.meta.env.VITE_APP_OAUTH_CLIENT_ID,
        //scope: "openid profile email",
        scope: 'openid',
        prompt: 'consent',
        access_type: 'offline',
        include_granted_scopes: false,
        ux_mode: 'popup',
        callback: async (response: any) => {
          console.log(response, 'requestAccountToken');
          if (requestToken) {
            const method = async () => {
              try {
                const info = await requestToken({
                  platform: CloudPlatform.googleCloud,
                  code: response?.code,
                });
                resolve(info as GoogleTokenInfo);
              } catch (err) {
                reject(err);
              }
            };
            method();
          } else {
            resolve(response);
          }
        },
        error_callback: (args) => {
          console.log(args, 'error_callback');
          reject(args);
        },
        ...restParams,
      });
    });
  };

  requestCloudToken = ({
    oauthClient = GoogleCloud.client,
    clientParams,
  }: RequestCloudTokenArgs): Promise<Result> => {
    return new Promise((resolve, reject) => {
      oauthClient?.requestToken({
        //client_secret: import.meta.env.VITE_APP_OAUTH_CLIENT_SECRET,
        scope: 'https://www.googleapis.com/auth/drive.appdata',
        prompt: '',
        ux_mode: 'redirect',
        callback: async (response: any) => {
          if (response?.error) {
            reject(response?.error);
            return;
          }
          resolve(response);
        },
        error_callback: (args) => {
          console.log(args, 'error_callback');
          reject(args);
        },
        ...clientParams,
      });
    });
  };

  initClient = (args: GoogleInitClientArgs): Promise<AuthInfo> => {
    const { key, cid, authInfo, clientParams, forcedUpdate = false } = args;
    this.key = key;
    this.cid = cid;
    this.clientParams = clientParams;

    return new Promise((resolve, reject) => {
      const fn = async () => {
        let oauthClient = null;
        if (this.authInfo && !forcedUpdate) {
          resolve(this.authInfo);
          return;
        } else {
          oauthClient = new GoogleOauth();
        }

        if (authInfo?.access_token) {
          oauthClient.setAccessToken(authInfo.access_token);
          GoogleCloud.client = oauthClient;
          this.authInfo = authInfo;
          resolve(this.authInfo);
          return;
        }
        try {
          if (clientParams.redirect_uri && clientParams.code) {
            // redirect mode
            this.authInfo = await this.requestAccessInfo({
              clientParams: clientParams as AccessInfoArgs,
            });
          } else {
            // popup mode and sure button
            if (clientParams.signInButton?.id) {
              await this.requestAccountToken({
                oauthClient,
                clientParams,
              });

              if (clientParams?.onPopup) {
                await clientParams.onPopup();
              }

              const signInDom = document.getElementById(clientParams.signInButton.id);
              if (!signInDom) {
                throw new Error('SignInDom not found');
              }
              if (clientParams.signInButton?.text) {
                signInDom!.innerHTML = clientParams.signInButton.text;
              }

              if (clientParams.signOutButton?.id) {
                const signOutDom = document.getElementById(clientParams.signOutButton.id);

                if (!signOutDom) {
                  throw new Error('SignOutDom not found');
                }
                if (clientParams.signOutButton?.text) {
                  signOutDom!.innerHTML = clientParams.signOutButton.text;
                }
                signOutDom?.addEventListener(
                  'click',
                  () => {
                    clientParams.signOutButton?.onCancel?.();
                    throw new Error(
                      JSON.stringify({
                        code: StatusCode.OUTH_LOGIN_CANCEL,
                        message: 'User cancel',
                      })
                    );
                  },
                  { once: true }
                );
              }

              this.authInfo = await waitForClick(signInDom, async () => {
                clientParams.signInButton?.onOk?.();
                return this.requestCloudToken({
                  oauthClient,
                  clientParams,
                });
              });
            } else {
              // popup mode
              await this.requestAccountToken({
                oauthClient,
                clientParams,
              });
              this.authInfo = await this.requestCloudToken({
                oauthClient,
                clientParams,
              });
            }
          }
          console.log(this.authInfo, 'this.authInfo');
          oauthClient.setAccessToken(this.authInfo!.access_token);
          GoogleCloud.client = oauthClient;
          resolve(this.authInfo);
        } catch (err: any) {
          if (err.type === 'popup_closed') {
            reject({ code: StatusCode.OUTH_POPUP_CANCEL, message: 'popup_closed' });
          }
          console.error(err, 'err');
          reject(err);
        }
      };
      fn();
    });
  };

  private readonly errorInterception = <Params, Response>(
    callback: (...args: Params[]) => Promise<Response>
  ) => {
    return async (...args: Params[]) => {
      try {
        return await callback(...args);
      } catch (err: any) {
        const error = safeJsonParse(err?.message || err, err?.message);
        console.error('errorInterception', error, err);
        if (error.status === 401) {
          const { token, refresh_token, expire } = await userApis.refreshGoogle({
            refresh_token: this.authInfo.refresh_token!,
          });
          if (token && refresh_token && expire) {
            const authInfo = { access_token: token, refresh_token, expire };
            await this.initClient({
              key: this.key,
              authInfo,
              clientParams: this.clientParams,
              forcedUpdate: true,
            });
            userApis.updateAuthParams({ authInfo });
            return await callback(...args);
          } else {
            throw Error(JSON.stringify({ status: 401, message: 'refresh token expired' }));
          }
        }

        throw error;
      }
    };
  };
  getUserInfo = this.errorInterception<void, OauthUserInfo>(async () => {
    if (!this.authInfo) {
      throw Error(JSON.stringify({ code: 401, message: 'unauthorized' }));
    }
    return await GoogleCloud.client!.getUserInfo();
  });

  upload = this.errorInterception<uploadArgs, any>(async (args: uploadArgs) => {
    const { isOldVersion, data } = args;
    if (!this.cid) {
      const userInfo = await this.getUserInfo();
      this.cid = asymmetricEncryption(userInfo.id!);
    }
    const metadata = this.getMetadata(isOldVersion ? undefined : this.cid);
    const fileName = metadata.name;
    if (!isPlainObject(data)) {
      throw Error('theDataShouldBeAnObjectType');
    }

    const file = new File([JSON.stringify(data)], fileName);

    const hasQuota = await GoogleCloud.client?.checkQuota(file.size);
    if (!hasQuota) {
      throw Error(JSON.stringify({ code: StatusCode.OUTH_UN_QUOTA, message: 'Quota exceeded' }));
    }

    const updateRes = await GoogleCloud?.client?.uploadFile({
      metadata,
      file,
    });

    if (updateRes && (this.existFiles as IFileListRes)?.files?.length) {
      Promise.all(
        (this.existFiles as IFileListRes)?.files?.map((item) =>
          GoogleCloud.client?.deleteFile(item.id).catch(console.log)
        )
      );
    }

    return updateRes;
  });

  updateWalletAccounts = this.errorInterception<
    any,
    {
      oldData: uploadArgs['data']['wallet'];
      newData: uploadArgs['data']['wallet'];
    }
  >(async (args: { isChange?: boolean; password?: string; accounts?: Account[]; t?: number }) => {
    const { isChange, password, accounts, t } = args;
    const oldData = await this.getCloudData();

    if (!oldData || JSON.stringify(oldData) === '{}') {
      throw Error('no data');
    }

    const oldWallet = oldData.wallet!;

    const isChange_ = isChange ?? !!oldWallet?.isChange;
    const password_ = isChange_ ? '' : password || oldWallet.password!;
    const accounts_ = accounts || oldWallet?.accounts;

    const newWalletData = await formatWalletData({
      ...oldWallet,
      isChange: isChange_,
      password: password_,
      accounts: accounts_,
    });

    const data = {
      ...oldData,
      wallet: t ? { ...newWalletData, t } : newWalletData,
    };

    await this.upload({
      data,
    });

    return {
      oldData: oldData.wallet as any,
      newData: data.wallet as any,
    };
  });

  updateWalletAccount = async (args: { account: Partial<Account> }) => {
    const { account } = args;
    const oldData = await this.getCloudData();

    if (!oldData || JSON.stringify(oldData) === '{}') {
      throw Error('no data');
    }

    const oldWallet = oldData.wallet!;

    const { accounts } = oldWallet;

    const oldAccountIndex = accounts.findIndex((item: Account) => item.id === account.id);

    if (oldAccountIndex === -1) {
      throw Error('account not found');
    }

    const oldAccount = accounts[oldAccountIndex];

    accounts.splice(oldAccountIndex, 1, Object.assign(oldAccount, account));

    const data = {
      ...oldData,
      wallet: await formatWalletData({
        ...oldWallet,
        accounts,
      }),
    };

    await this.upload({
      data,
    });

    return {
      oldData: oldData.wallet as any,
      newData: data.wallet as any,
    };
  };

  saveWalletAccount = this.errorInterception<
    SaveAccountArgs,
    {
      oldData: uploadArgs['data']['wallet'];
      newData: uploadArgs['data']['wallet'];
    }
  >(async (args: SaveAccountArgs) => {
    const { account } = args;
    const oldData = await this.getCloudData();

    if (!oldData || JSON.stringify(oldData) === '{}') {
      throw Error('no data');
    }

    const oldWallet = oldData.wallet!;

    const data = {
      ...oldData,
      wallet: await formatWalletData({
        ...oldWallet,
        accounts: [...oldWallet.accounts, account],
      }),
    };

    await this.upload({
      data,
    });

    return {
      oldData: oldData.wallet as any,
      newData: data.wallet as any,
    };
  });

  deleteWalletAccount = this.errorInterception<
    DeleteAccountArgs,
    {
      oldData: uploadArgs['data']['wallet'];
      newData: uploadArgs['data']['wallet'];
    }
  >(async (args: DeleteAccountArgs) => {
    const { id } = args;
    const oldData = await this.getCloudData();

    if (!oldData || JSON.stringify(oldData) === '{}') {
      throw Error('no data');
    }

    const oldWallet = oldData.wallet!;

    const data = {
      ...oldData,
      wallet: await formatWalletData({
        ...oldWallet,
        accounts: oldWallet?.accounts?.filter((item) => item.id !== id) || [],
      }),
    };

    await this.upload({ data });

    return {
      oldData: oldData.wallet as any,
      newData: data.wallet as any,
    };
  });

  deleteCloudData = this.errorInterception<
    DeleteCloudDataArgs,
    {
      oldData: uploadArgs['data'];
      newData: uploadArgs['data'];
    }
  >(async (args?: DeleteCloudDataArgs) => {
    const { keys } = args ?? {};
    let oldData = await this.getCloudData();

    if (!oldData || JSON.stringify(oldData) === '{}') {
      throw Error('no data');
    }

    if (!keys?.length) {
      oldData = {};
    } else {
      for (const value of keys) {
        delete oldData[value];
      }
    }

    const data = {
      ...oldData,
    };

    await this.upload({ data });

    return {
      oldData: oldData,
      newData: data,
    };
  });

  getWalletAccounts = this.errorInterception<void, Account[]>(async () => {
    const data = await this.getCloudData();
    return data?.wallet?.accounts || [];
  });

  getPassword = this.errorInterception<void, { password?: string; isChange?: boolean }>(
    async () => {
      const oldCloudData = await this.getCloudData();
      return {
        password: oldCloudData?.wallet?.password,
        isChange: oldCloudData?.wallet?.isChange,
      };
    }
  );

  getCloudData = this.errorInterception<{ cid: string }, Partial<uploadArgs['data']>>(
    async (params) => {
      let { cid } = params ?? { cid: this.cid };
      if (!cid) {
        const userInfo = await this.getUserInfo();
        cid = asymmetricEncryption(userInfo.id!);
      }
      this.cid = cid;
      const metadata = this.getMetadata(cid);
      const existFiles = await GoogleCloud.client?.getDriveFiles(metadata);
      let privateFile: any = (existFiles as IFileListRes)?.files?.[0];
      let isNewData = true;

      if (!privateFile) {
        const metadata = this.getMetadata();
        const existFiles = await GoogleCloud.client?.getDriveFiles(metadata);
        privateFile = (existFiles as IFileListRes)?.files?.[0];
        isNewData = false;
        if (!privateFile) {
          return {};
        }
      }

      this.existFiles = existFiles as IFileListRes;

      const res = await GoogleCloud.client?.getFileContent(privateFile?.id);
      const data = typeof res === 'string' ? safeJsonParse(res, {}) : (res as any);
      if (!isNewData) {
        const newAccounts = WalletCore.migrateAccounts(data.accounts?.[cid!] || []);
        const newData = {
          wallet: await formatWalletData({
            ...data,
            accounts: newAccounts,
          }),
        };
        await this.upload({
          isOldVersion: true,
          data: {} as any,
        });
        await this.upload({
          data: newData,
        });
        return newData;
      }

      if (
        data?.wallet?.accounts?.length &&
        (data.wallet.accounts.some((account: any) => 'seriesWallets' in account) ||
          data?.wallet?.version === 1)
      ) {
        const newAccounts = WalletCore.migrateAccounts(data.wallet.accounts || []);

        const newInvalidAccounts = data.wallet?.invalidAccounts?.length
          ? WalletCore.migrateAccounts(data.wallet?.invalidAccounts)
          : [];

        const backupCloudData = safeJsonParse(data.wallet?.backupCloudData || '', {} as any);
        const newBackupCloudDataAccounts = backupCloudData?.accounts?.length
          ? WalletCore.migrateAccounts(backupCloudData.accounts)
          : [];

        const newWallet = await formatWalletData({
          ...data.wallet,
          accounts: newAccounts,
          invalidAccounts: newInvalidAccounts,
        });

        const newData = {
          ...data,
          wallet: {
            ...newWallet,
            backupCloudData: newBackupCloudDataAccounts.length
              ? JSON.stringify({
                  ...backupCloudData,
                  t: newWallet.t,
                  version: newWallet.version,
                  accounts: formatAccountsData(newBackupCloudDataAccounts),
                })
              : '',
          },
        };
        await this.upload({
          data: newData,
        });
        return newData;
      }
      return data;
    }
  );
}
